import React, { useEffect, useState } from "react";
import { Play, Calendar, Star, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useBranding } from "@/hooks/useBranding";

interface Announcement {
    id: string;
    title: string;
    description: string;
    image_url?: string;
    mobile_image_url?: string;
    layout?: "standard" | "campaign";
    call_to_action_text?: string;
    call_to_action_url?: string;
    priority: number;
    published_at: string;
    expires_at?: string;
}

interface AnnouncementModalProps {
    isOpen: boolean;
    onClose: (action?: "clicked_cta" | "dismissed") => void;
    announcement: Announcement;
}

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({ isOpen, onClose, announcement }) => {
    const branding = useBranding();
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleAction = () => {
        if (announcement.call_to_action_url) {
            window.open(announcement.call_to_action_url, "_blank");
        }

        onClose("clicked_cta");
    };

    const handleDismiss = () => {
        onClose("dismissed");
    };

    useEffect(() => {
        // Reset image states when announcement changes
        setImageLoaded(false);
        setImageError(false);
    }, [announcement.id]);

    const isCampaign = announcement.layout === "campaign" && Boolean(announcement.image_url);

    return (
        <Dialog open={isOpen} onOpenChange={(open) => {
            if (!open) handleDismiss();
        }}>
            <DialogContent className={`${isCampaign ? "max-w-5xl" : "max-w-2xl"} max-h-[92vh] p-0 overflow-y-auto bg-white rounded-2xl shadow-2xl`}>
                <DialogTitle className="sr-only">{announcement.title}</DialogTitle>
                {isCampaign ? (
                    <div className="bg-white">
                        <picture className="block bg-slate-100">
                            {announcement.mobile_image_url && (
                                <source media="(max-width: 639px)" srcSet={announcement.mobile_image_url} />
                            )}
                            <img
                                src={announcement.image_url}
                                alt={announcement.title}
                                className="block h-auto max-h-[72vh] w-full object-contain"
                                onLoad={() => setImageLoaded(true)}
                                onError={() => setImageError(true)}
                            />
                        </picture>
                        <div className="border-t bg-white p-3 sm:p-4">
                            <Button
                                onClick={handleAction}
                                className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-orange-400 py-6 text-base font-bold text-white shadow-lg transition hover:brightness-105 sm:text-lg"
                            >
                                <Play className="mr-2 h-5 w-5" />
                                {announcement.call_to_action_text || "Got it"}
                            </Button>
                        </div>
                    </div>
                ) : (
                <div className="relative">
                    <div className="flex flex-col md:flex-row">
                        {/* Image Section */}
                        {announcement.image_url && !imageError && (
                            <div className="md:w-1/2 bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center p-8 min-h-[300px]">
                                {!imageLoaded && (
                                    <div className="animate-pulse bg-gray-200 rounded-lg w-full h-64" />
                                )}
                                <img
                                    src={announcement.image_url}
                                    alt={announcement.title}
                                    className={`w-full h-auto object-contain rounded-lg transition-opacity duration-300 ${
                                        imageLoaded ? "opacity-100" : "opacity-0"
                                    }`}
                                    onLoad={() => setImageLoaded(true)}
                                    onError={() => setImageError(true)}
                                />
                            </div>
                        )}

                        {/* Content Section */}
                        <div className={`flex-1 p-8 ${announcement.image_url && !imageError ? "md:w-1/2" : "w-full"}`}>
                            {/* Logo/Brand */}
                            <div className="flex items-center gap-2 mb-6">
                                <img
                                    src={branding.compactLogoUrl}
                                    alt={branding.logoAlt}
                                    className="h-10 w-10 rounded-lg object-cover"
                                />
                                <span className="text-xl font-bold text-gray-900">
                                    Qit Playlist
                                </span>
                            </div>

                            {/* Title */}
                            <h2 className="text-2xl font-bold text-gray-900 mb-3">
                                {announcement.title}
                            </h2>

                            {/* Description */}
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                {announcement.description}
                            </p>

                            {/* Info Section */}
                            <div className="bg-blue-50 rounded-lg p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <ShieldCheck className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-semibold text-blue-900 mb-1">What you need to know</h3>
                                        <p className="text-sm text-blue-700">
                                            Some features, including payments and verification, may require account confirmation before use.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer Message */}
                            <div className="flex items-center gap-2 mb-6 text-sm text-gray-500">
                                <Calendar className="h-4 w-4" />
                                <Star className="h-4 w-4" />
                                <span>Thank you for being part of Qit Playlist! Your support helps us keep improving.</span>
                            </div>

                            {/* Action Button */}
                            <Button
                                onClick={handleAction}
                                className="w-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 hover:from-blue-600 hover:via-purple-600 hover:to-pink-600 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                            >
                                <Play className="mr-2 h-5 w-5" />
                                {announcement.call_to_action_text || "Got it"}
                            </Button>
                        </div>
                    </div>
                </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default AnnouncementModal;
